import { useSortable } from '@dnd-kit/sortable';
import { Popover, Transition } from '@headlessui/react';
import {
  ArrowNarrowRightIcon,
  MinusCircleIcon,
  TrendingDownIcon,
  TrendingUpIcon,
  XIcon,
} from '@heroicons/react/outline';
import type { ChartOptions, Tick } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { ChartBase } from 'components/common';
import {
  ChevronDownIcon,
  DragIconIndicator,
  ExternalLinkIcon,
  InformationIcon,
} from 'components/icons';
import { CardBackground, Skeleton, Tooltip } from 'components/ui';
import { format, parse } from 'date-fns';
import { orderBy } from 'lodash';
import { Fragment, useMemo } from 'react';
import { useAppSelector } from 'store';
import { useGetCustomerStatisticsQuery } from 'store/services/segmentsApi';
import type { CustomerStatistics, TimeSeries } from 'store/services/types';
import { userDetailsState } from 'store/userDetailsSlice';
import type { Metric } from './types';
import { useStoreMetrics } from './useStoreMetrics';

export const MetricsStatsCard = ({ metric }: { metric: Metric }) => {
  const { currentStore } = useAppSelector(userDetailsState);

  const { attributes, listeners } = useSortable({ id: metric.id });

  const {
    selectedDateRange,
    selectedGroupBy,
    handleRemoveMetric,
    handleExpandCard,
    expandAllCards,
  } = useStoreMetrics();

  const currency = currentStore.completeInfo?.currency ?? 'usd';
  const locale = currentStore.completeInfo?.primary_locale ?? 'en';

  const { data, isFetching } = useGetCustomerStatisticsQuery(
    {
      body: metric.filters ? metric.filters : {},
      params: {
        store_name: String(currentStore.completeInfo?.store_name),
        metric_list: [metric.id as keyof CustomerStatistics],
        start_date: selectedDateRange.start_date,
        end_date: selectedDateRange.end_date,
        ticking: selectedGroupBy,
      },
    },
    { skip: !!!currentStore.completeInfo?.store_name }
  );

  const metricTimeseries = useMemo(() => {
    const timeseries = ((data && data[metric.id as keyof CustomerStatistics]) ||
      []) as TimeSeries[];

    const orderedTimeseries = orderBy(
      timeseries,
      (item) => parse(item?.date || '', 'yyyy-MM-dd', Date.now()),
      ['asc']
    ).filter((item) => item?.value);

    return orderedTimeseries;
  }, [data, metric.id]);

  const {
    data: staticMetric,
    isFetching: staticIsFetching,
    refetch: staticMetricRefresh,
  } = useGetCustomerStatisticsQuery(
    {
      body: metric.filters ? metric.filters : {},
      params: {
        store_name: String(currentStore.completeInfo?.store_name),
        metric_list: [metric.id as keyof CustomerStatistics],
        start_date: selectedDateRange.start_date,
        end_date: selectedDateRange.end_date,
      },
    },
    { skip: !!!currentStore.completeInfo?.store_name }
  );

  const chartData = useMemo(() => {
    const preparedData = Array.isArray(metricTimeseries)
      ? metricTimeseries.map((item) => {
          return {
            x: 'date' in item ? item.date : format(new Date(), 'yyyy-MM-dd'),
            y: item.value,
          };
        })
      : [];

    return {
      datasets: [
        {
          label: metric.id,
          data: preparedData,
          backgroundColor: '#125970',
          borderColor: '#125970',
          borderWidth: 3,
          pointStyle: '',
        },
      ],
    };
  }, [metric.id, metricTimeseries]);

  const options: ChartOptions = {
    interaction: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      x: {
        offset: metricTimeseries.length > 1 ? false : true,
        type: 'time',
        time: {
          unit: selectedGroupBy,
          tooltipFormat: 'MMM dd',
          displayFormats: {
            day: 'MMM dd',
            week: 'MMM dd',
            month: 'MMM',
          },
        },
      },
      y: {
        ticks: {
          callback: (
            tickValue: string | number,
            index: number,
            ticks: Tick[]
          ) => {
            if (metric.format === 'currency') {
              return tickValue.toLocaleString(locale, {
                style: 'currency',
                currency: currency,
                minimumFractionDigits: tickValue >= 10 ? 0 : 2,
                maximumFractionDigits: tickValue >= 10 ? 0 : 2,
              });
            }

            if (metric.format === 'percent') {
              return tickValue.toLocaleString(locale, {
                style: 'percent',
                minimumFractionDigits: tickValue <= 1 ? 2 : 0,
                maximumFractionDigits: tickValue <= 1 ? 2 : 0,
              });
            }

            return tickValue.toLocaleString(locale, {
              minimumFractionDigits: tickValue >= 10 ? 0 : 2,
              maximumFractionDigits: tickValue >= 10 ? 0 : 2,
            });
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = metric.label || context.dataset.label || '';

            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += formatMetricDisplay(context.parsed.y);
            }
            return label;
          },
        },
      },
    },
    elements: {
      point: {
        radius: metricTimeseries.length > 1 ? 3 : 6,
        hoverRadius: 6,
      },
    },
  };

  function formatMetricDisplay(value: number | unknown) {
    if (typeof value !== 'number') {
      return;
    }

    if (metric.format === 'percent') {
      return value.toLocaleString(locale, {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    }

    if (metric.format === 'currency') {
      return value.toLocaleString(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: value > 10 ? 0 : 2,
      });
    }
    return value.toLocaleString(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: value > 10 ? 0 : 2,
    });
  }

  const staticMetricValue = formatMetricDisplay(
    staticMetric &&
      staticMetric[metric.id as keyof CustomerStatistics]?.length &&
      staticMetric[metric.id as keyof CustomerStatistics]![0].value
  );

  const open = expandAllCards || metric.expandedCard;

  const metricDifference = useMemo(() => {
    if (metricTimeseries.length < 2) {
      return 0;
    }

    const oldest = metricTimeseries[0];
    const newest = metricTimeseries[metricTimeseries.length - 1];

    return (newest.value - oldest.value) / (oldest.value || 1);
  }, [metricTimeseries]);

  return (
    <div>
      <CardBackground>
        <div>
          <div className="p-10">
            <div className="w-full text-left">
              <div className="flex items-center justify-between ">
                <div className="flex items-center space-x-1">
                  <p className="text-base text-black text-opacity-50">
                    {metric.label}
                  </p>
                  <div>
                    {metric.description && (
                      <DescriptionPopOver
                        description={metric.description}
                        learnMoreLink={metric.learnMoreUrl}
                      />
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <Tooltip text="Expand / collapse card">
                    <button onClick={() => handleExpandCard(metric)}>
                      <ChevronDownIcon
                        className={`w-5 h-5 text-gray-600 ${
                          metric.expandedCard && 'rotate-180'
                        }`}
                      />
                    </button>
                  </Tooltip>
                  <Tooltip text="Remove metric">
                    <button onClick={() => handleRemoveMetric(metric)}>
                      <MinusCircleIcon className="w-5 h-5 text-gray-500" />
                    </button>
                  </Tooltip>
                  <Tooltip text="Drag to rearrange">
                    <DragIconIndicator
                      className="w-5 h-5 outline-none"
                      {...attributes}
                      {...listeners}
                    />
                  </Tooltip>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-[32px]">
                  <Skeleton isLoaded={!!!staticIsFetching}>
                    {staticMetricValue || (
                      <div>
                        <p className="">
                          Failed to load -{' '}
                          <span>
                            <button
                              className="text-segments-link font-semibold"
                              onClick={staticMetricRefresh}
                            >
                              Retry
                            </button>
                          </span>
                        </p>
                      </div>
                    )}
                  </Skeleton>
                </div>
              </div>
              <div className="mt-0.5">
                <MetricChange data={metricDifference} loading={isFetching} />
              </div>
            </div>
            {open && (
              <div className="mt-4">
                {isFetching ? (
                  <Skeleton isLoaded={false}>
                    <div className="flex items-center justify-center h-[240px]"></div>
                  </Skeleton>
                ) : (
                  <div className="mt-4">
                    <ChartBase type="line" data={chartData} options={options} />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </CardBackground>
    </div>
  );
};

function DescriptionPopOver(props: {
  description: string;
  learnMoreLink?: string;
}) {
  return (
    <Popover
      className="relative"
      onClick={(e: { stopPropagation: () => void }) => e.stopPropagation()}
    >
      {({ open, close }) => (
        <>
          <Popover.Button className="flex items-center justify-center focus:outline-none">
            <div className=" hover:bg-segments-gray rounded p-0.5">
              <InformationIcon className="w-4 h-4 opacity-50" />
            </div>
          </Popover.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute z-10 w-screen max-w-sm p-0 px-4 mt-2 transform -translate-x-1/2 left-1/2 sm:px-0">
              <div className="overflow-hidden rounded-lg shadow-2xl ring-1 ring-black ring-opacity-10">
                <div className="pt-4 bg-white px-7 pb-7 bg-opacity-98">
                  <div className="flex justify-end">
                    <button
                      className="focus:outline-none"
                      onClick={() => close()}
                    >
                      <XIcon className="w-5 h-5" />
                    </button>
                  </div>
                  <p>
                    {props.description}

                    {props.learnMoreLink && (
                      <>
                        <br />
                        <br />
                        <span>
                          <a
                            href={props.learnMoreLink}
                            className="text-segments-link"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Learn more{' '}
                            <ExternalLinkIcon className="inline w-5 h-5 align-middle" />
                          </a>
                        </span>
                      </>
                    )}
                  </p>
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

function MetricChange({ data, loading }: { data: number; loading: boolean }) {
  if (loading === true) {
    return (
      <div className="flex items-center space-x-1.5">
        <Skeleton isLoaded={false}>
          <ArrowNarrowRightIcon className="w-1 h-1 text-gray-600" />
          <p className="text-gray-500">
            %{'  '}
            <span className="text-black text-opacity-50 text-segments-13px">
              since starting period
            </span>{' '}
          </p>
        </Skeleton>
      </div>
    );
  }

  if (data === 0) {
    return (
      <div className="flex items-center space-x-1.5">
        <ArrowNarrowRightIcon className="w-5 h-5 text-gray-600" />
        <p className="text-gray-500">
          {data}%{'  '}
          <span className="text-black text-opacity-50 text-segments-13px">
            since starting period
          </span>{' '}
        </p>
      </div>
    );
  }

  if (data > 0) {
    return (
      <div className="flex items-center space-x-2">
        <TrendingUpIcon className="w-5 h-5 text-green-500" />
        <p className="text-green-500">
          +
          {data.toLocaleString('en-US', {
            style: 'percent',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          })}
          <span className="text-black text-opacity-50 text-segments-13px">
            {' '}
            since starting period
          </span>{' '}
        </p>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2">
      <TrendingDownIcon className="w-5 h-5 text-segments-danger" />
      <p className="text-segments-danger">
        {data.toLocaleString('en-US', {
          style: 'percent',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })}
        <span className="text-black text-opacity-50 text-segments-13px">
          {' '}
          since starting period
        </span>{' '}
      </p>
    </div>
  );
}
